<template>
  <div class="ov-page">
    <div class="ov-header">
      <div>
        <div class="ov-header__kicker rb-kicker">Nouvelle réservation</div>
        <h1 class="ov-header__title">{{ offreVoyage.designation || '—' }}</h1>
        <div class="rb-route">
          <span>{{ offreVoyage.villeDepartDesignation || '—' }}</span>
          <span class="rb-route__line"></span>
          <span>{{ offreVoyage.villeDestinationDesignation || '—' }}</span>
        </div>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--secondary" @click="$router.push({ path: '/offre_voyage_details' })">
          <v-icon small>mdi-arrow-left</v-icon>
          Programmes
        </button>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== ASSISTANT ===== -->
      <div class="ov-card">
        <!-- Étapes -->
        <div class="rb-steps">
          <div
            v-for="(label, idx) in steps"
            :key="label"
            class="rb-step-wrap"
            :class="{ 'rb-step-wrap--last': idx === steps.length - 1 }"
          >
            <div
              class="rb-step"
              :class="{
                'rb-step--active': currentStep === idx + 1,
                'rb-step--done': currentStep > idx + 1,
                'rb-step--clickable': currentStep > idx + 1,
              }"
              @click="currentStep > idx + 1 ? (currentStep = idx + 1) : null"
            >
              <span class="rb-step__num">
                <v-icon v-if="currentStep > idx + 1" x-small color="white">mdi-check</v-icon>
                <template v-else>{{ idx + 1 }}</template>
              </span>
              <span class="rb-step__label">{{ label }}</span>
            </div>
            <div v-if="idx < steps.length - 1" class="rb-step__line" :class="{ 'rb-step__line--done': currentStep > idx + 1 }"></div>
          </div>
        </div>

        <!-- 1. PROGRAMME -->
        <div v-show="currentStep === 1">
          <div class="ov-card__body">
            <div class="rb-panel__title">Choisissez le programme</div>
            <div v-if="activeProgrammeList.length == 0" class="ov-empty">Aucun programme en cours pour cette offre.</div>
            <div v-else class="rb-options">
              <button
                v-for="(programme, index) in activeProgrammeList"
                :key="programme.id || index"
                type="button"
                class="rb-option rb-option--programme"
                :class="{
                  'rb-option--selected': progChoosen === programme,
                  'rb-option--disabled': isFull(programme),
                }"
                :disabled="isFull(programme)"
                @click="selectProgramme(programme)"
              >
                <span class="rb-radio"></span>
                <span class="rb-prog__date">
                  <span class="rb-prog__day">{{ programme.dateDepart }}</span>
                  <span class="rb-prog__hour">{{ programme.heureDepart || '—' }}</span>
                </span>
                <span class="rb-prog__main">
                  <span class="rb-prog__title">{{ programme.designation }}</span>
                  <span class="rb-prog__meta">
                    Arrivée {{ programme.dateArrivee || '—' }}<template v-if="programme.heureArrivee"> · {{ programme.heureArrivee }}</template>
                    · Bus {{ programme.numeroBus || '—' }}
                  </span>
                </span>
                <span v-if="isFull(programme)" class="ov-badge ov-badge--danger">Complet</span>
                <span
                  v-else-if="programme.nombrePlaceDisponible !== null && programme.nombrePlaceDisponible !== undefined"
                  class="ov-badge ov-badge--success"
                >
                  {{ programme.nombrePlaceDisponible }} places
                </span>
              </button>
            </div>
          </div>
          <div class="ov-card__foot">
            <button type="button" class="ov-btn ov-btn--secondary" @click="$router.push({ path: '/offre_voyage_details' })">Annuler</button>
            <button type="button" class="ov-btn ov-btn--primary" :disabled="!progChoosen.designation" @click="currentStep = 2">
              Continuer
              <v-icon small>mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>

        <!-- 2. TARIF -->
        <div v-show="currentStep === 2">
          <div class="ov-card__body">
            <div class="rb-panel__title">Choisissez le tarif</div>
            <div v-if="prixEtModeParOffreVoyageList.length == 0" class="ov-empty">Aucun tarif défini pour cette offre.</div>
            <div v-else class="rb-tarifs">
              <button
                v-for="(mode, index) in prixEtModeParOffreVoyageList"
                :key="index"
                type="button"
                class="rb-option rb-option--tarif"
                :class="{ 'rb-option--selected': modeChoosen === mode }"
                @click="modeChoosen = mode"
              >
                <span class="rb-tarif__top">
                  <span class="rb-tarif__category">{{ mode.categorieVoyageurDesignation }}</span>
                  <span class="rb-radio"></span>
                </span>
                <span class="rb-tarif__price">{{ formatAmount(mode.prix) }} <small>FCFA</small></span>
                <span class="rb-tarif__mode">{{ mode.modeDesignation }}</span>
              </button>
            </div>
          </div>
          <div class="ov-card__foot">
            <button type="button" class="ov-btn ov-btn--secondary" @click="currentStep = 1">
              <v-icon small>mdi-arrow-left</v-icon>
              Retour
            </button>
            <button type="button" class="ov-btn ov-btn--primary" :disabled="!modeChoosen.modeDesignation" @click="currentStep = 3">
              Continuer
              <v-icon small>mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>

        <!-- 3. GARE (ADMIN) -->
        <div v-if="isAdmin" v-show="currentStep === 3">
          <div class="ov-card__body">
            <div class="rb-panel__title">Gare de départ du client</div>
            <div class="ov-field rb-narrow">
              <label class="ov-label" for="rb-gare">Gare de transport<span class="ov-required">*</span></label>
              <select id="rb-gare" v-model="selectedGare" class="ov-select">
                <option value="" disabled>Sélectionnez une gare</option>
                <option v-for="(gare, index) in garesList" :key="index" :value="gare.designation">{{ gare.designation }}</option>
              </select>
            </div>
          </div>
          <div class="ov-card__foot">
            <button type="button" class="ov-btn ov-btn--secondary" @click="currentStep = 2">
              <v-icon small>mdi-arrow-left</v-icon>
              Retour
            </button>
            <button type="button" class="ov-btn ov-btn--primary" :disabled="!selectedGare" @click="currentStep = 4">
              Continuer
              <v-icon small>mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>

        <!-- CLIENT -->
        <div v-show="currentStep === clientStep">
          <div class="ov-card__body">
            <div class="rb-panel__title">Informations du voyageur</div>
            <div class="ov-row">
              <div class="ov-field">
                <label class="ov-label" for="rb-nom">Nom<span class="ov-required">*</span></label>
                <input id="rb-nom" v-model.trim="user.nom" type="text" class="ov-input" :class="{ 'is-invalid': clientSubmitted && clientErrors.nom }" placeholder="Nom du voyageur" autocomplete="off" />
                <div v-if="clientSubmitted && clientErrors.nom" class="ov-error">{{ clientErrors.nom }}</div>
              </div>
              <div class="ov-field">
                <label class="ov-label" for="rb-prenoms">Prénom(s)<span class="ov-required">*</span></label>
                <input id="rb-prenoms" v-model.trim="user.prenoms" type="text" class="ov-input" :class="{ 'is-invalid': clientSubmitted && clientErrors.prenoms }" placeholder="Prénom(s) du voyageur" autocomplete="off" />
                <div v-if="clientSubmitted && clientErrors.prenoms" class="ov-error">{{ clientErrors.prenoms }}</div>
              </div>
              <div class="ov-field">
                <label class="ov-label" for="rb-tel">Téléphone<span class="ov-required">*</span></label>
                <div class="ov-input-group">
                  <span class="ov-input-prefix">+225</span>
                  <input id="rb-tel" v-model.trim="user.telephone" type="tel" inputmode="tel" class="ov-input" :class="{ 'is-invalid': clientSubmitted && clientErrors.telephone }" placeholder="07 07 07 07 07" autocomplete="off" />
                </div>
                <div v-if="clientSubmitted && clientErrors.telephone" class="ov-error">{{ clientErrors.telephone }}</div>
              </div>
              <div class="ov-field">
                <label class="ov-label" for="rb-email">E-mail</label>
                <input id="rb-email" v-model.trim="user.email" type="email" class="ov-input" :class="{ 'is-invalid': clientSubmitted && clientErrors.email }" placeholder="Facultatif" autocomplete="off" />
                <div v-if="clientSubmitted && clientErrors.email" class="ov-error">{{ clientErrors.email }}</div>
              </div>
              <div class="ov-field">
                <label class="ov-label" for="rb-places">Nombre de places<span class="ov-required">*</span></label>
                <input
                  id="rb-places"
                  v-model.number="programmeReserved.data.nombrePlace"
                  type="number"
                  min="1"
                  :max="availableSeats || undefined"
                  class="ov-input"
                  :class="{ 'is-invalid': clientSubmitted && clientErrors.places }"
                  placeholder="1"
                />
                <div v-if="clientSubmitted && clientErrors.places" class="ov-error">{{ clientErrors.places }}</div>
                <div v-else-if="availableSeats !== null" class="ov-hint">{{ availableSeats }} place(s) disponible(s) sur ce programme.</div>
              </div>
            </div>
          </div>
          <div class="ov-card__foot">
            <button type="button" class="ov-btn ov-btn--secondary" @click="currentStep = isAdmin ? 3 : 2">
              <v-icon small>mdi-arrow-left</v-icon>
              Retour
            </button>
            <button type="button" class="ov-btn ov-btn--primary" :disabled="overlay" @click="reserverOffre()">
              <v-icon small>mdi-check</v-icon>
              Confirmer la réservation
            </button>
          </div>
        </div>
      </div>

      <!-- ===== RÉCAPITULATIF ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">Récapitulatif</div>
          </div>
          <div class="ov-card__body">
            <div class="ov-stat">
              <span class="ov-stat__label">Programme</span>
              <span class="ov-stat__value">
                <template v-if="progChoosen.designation">{{ progChoosen.dateDepart }} · {{ progChoosen.heureDepart }}</template>
                <template v-else>—</template>
              </span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Bus</span>
              <span class="ov-stat__value">{{ progChoosen.numeroBus || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Tarif</span>
              <span class="ov-stat__value">{{ modeChoosen.categorieVoyageurDesignation || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Prix unitaire</span>
              <span class="ov-stat__value">{{ modeChoosen.prix ? formatAmount(modeChoosen.prix) + ' FCFA' : '—' }}</span>
            </div>
            <div v-if="isAdmin" class="ov-stat">
              <span class="ov-stat__label">Gare</span>
              <span class="ov-stat__value">{{ selectedGare || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Voyageur</span>
              <span class="ov-stat__value">{{ [user.nom, user.prenoms].filter(Boolean).join(' ') || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Places</span>
              <span class="ov-stat__value">{{ programmeReserved.data.nombrePlace || '—' }}</span>
            </div>
          </div>
          <div class="rb-total">
            <span>Total à payer</span>
            <strong>{{ totalPrice ? formatAmount(totalPrice) : '—' }} <small v-if="totalPrice">FCFA</small></strong>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-warning" type="warning" width="350px" dismissible>{{ warningMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_RECUPERER_PRIX_PAR_OFFRE_VOYAGE,
  API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
  API_RESERVER_PROGRAMME_OFFRE,
  API_CREATE_HISTORIQUE_PAIEMENT,
  HEADERS,
} from "../globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "@/modules/messages/messageProcess";
import { ROLE_ADMIN_COMPAGNIE_TRANSPORT } from "../globalConfig/constUsersRoles";
import apiService from "@/services/api.service";
export default {
  name: "ReservationBillet",
  data() {
    return {
      successMsg: null,
      errorMsg: null,

      modeChoosen: {},

      progChoosen: {},

      programmeList: [],

      programmeReserved: {
        data: {
          programmeDesignation: null,
          nombrePlace: null,
          categorieVoyageur: null,
          gareDesignation: null,
          compagnieTransportRaisonSociale: null,
          isOtherPerson: true,
          clientDetails: {
            nom: null,
            prenoms: null,
            email: null,
            telephone: null,
          },
          offreVoyageDesignation: null,
        },
      },

        historyPaiement: {
        data: {
          dateTimePayment: "",
          modePaiementDesignation: "",
          reservationBilletVoyageDesignation: "",
        },
      },

      user: {
        nom: null,
        prenoms: null,
        email: null,
        telephone: null,
      },

      warningMsg: null,
      overlay: false,
      // Affiche les erreurs du formulaire client après une tentative de validation
      clientSubmitted: false,

      offreVoyage: {
        id: null,
        designation: null,
        description: null,
        compagnieTransportRaisonSociale: null,
        typeOffreVoyageDesignation: null,
        villeDepartDesignation: null,
        villeDestinationDesignation: null,
      },

      prixEtModeParOffreVoyageList: [],

      offreVoyageObject: {
        data: {
          designation: null,
        },
      },

      currentStep: 1,
      selectedGare: "",
      garesList: [],
      gareListObject: {
        data: {
          compagnieTransportRaisonSociale: null,
        },
      },
    };
  },

  computed: {
    isAdmin() {
      return this.$store.state.userAuthentified?.roleCode === ROLE_ADMIN_COMPAGNIE_TRANSPORT;
    },
    activeProgrammeList() {
      if (!Array.isArray(this.programmeList)) return [];
      return this.programmeList.filter((p) => p && p.isExpired !== true);
    },
    steps() {
      return this.isAdmin ? ["Programme", "Tarif", "Gare", "Client"] : ["Programme", "Tarif", "Client"];
    },

    clientStep() {
      return this.isAdmin ? 4 : 3;
    },

    availableSeats() {
      const value = this.progChoosen && this.progChoosen.nombrePlaceDisponible;
      return value === null || value === undefined || value === "" ? null : Number(value);
    },

    seatsError() {
      const places = Number(this.programmeReserved.data.nombrePlace);
      if (!this.programmeReserved.data.nombrePlace) return "Indiquez le nombre de places.";
      if (!Number.isInteger(places) || places < 1) return "Le nombre de places doit être un entier supérieur à 0.";
      if (this.availableSeats !== null && places > this.availableSeats) {
        return `Il ne reste que ${this.availableSeats} place(s) sur ce programme.`;
      }
      return null;
    },

    clientErrors() {
      return {
        nom: !this.user.nom ? "Le nom est obligatoire." : null,
        prenoms: !this.user.prenoms ? "Le prénom est obligatoire." : null,
        telephone: !this.user.telephone ? "Le numéro de téléphone est obligatoire." : null,
        email: this.user.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.user.email) ? "Adresse e-mail invalide." : null,
        places: this.seatsError,
      };
    },

    clientValid() {
      return Object.values(this.clientErrors).every((e) => !e);
    },

    totalPrice() {
      const price = Number(this.modeChoosen && this.modeChoosen.prix);
      const places = Number(this.programmeReserved.data.nombrePlace);
      return price > 0 && places > 0 ? price * places : null;
    },
  },

  methods: {
    // FAIRE UNE RESERVATION POUR UN UTILISATEUR
    async reserverOffre() {
      this.clientSubmitted = true;
      if (!this.clientValid) return;
      this.$swal
        .fire({
          title: "Confirmer la réservation ?",
          text: `${this.programmeReserved.data.nombrePlace} place(s) · ${this.formatAmount(this.totalPrice)} FCFA`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#159e72",
          confirmButtonText: "Confirmer",
          cancelButtonText: "Annuler",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.overlay = true;
            
            // Vérifier que compagnieTransportRaisonSociale est défini
            const compagnieRaisonSociale = this.$store.state.userAuthentified?.compagnieTransportRaisonSociale;
            
            if (!compagnieRaisonSociale) {
              this.overlay = false;
              this.$swal.fire({
                title: "Erreur",
                text: "Impossible de récupérer les informations de votre compagnie. Veuillez vous reconnecter.",
                icon: "error"
              });
              return;
            }
            
            this.programmeReserved.data.programmeDesignation =
              this.progChoosen.designation;
            this.programmeReserved.data.gareDesignation =
              this.isAdmin ? this.selectedGare : this.$store.state.userAuthentified.gareDesignation;
            this.programmeReserved.data.offreVoyageDesignation =
              this.offreVoyage.designation;
            this.programmeReserved.data.categorieVoyageur =
              this.modeChoosen.categorieVoyageurDesignation;
            this.programmeReserved.data.compagnieTransportRaisonSociale = compagnieRaisonSociale;
            this.programmeReserved.data.clientDetails = this.user;
            
            // Indiquer au backend que c'est un admin qui fait la réservation
            if (this.isAdmin) {
              this.programmeReserved.data.isAdminReservation = true;
            }

            // Vérification supplémentaire pour les admins
            if (this.isAdmin && !this.selectedGare) {
              this.overlay = false;
              this.$swal.fire({
                title: "Erreur",
                text: "Veuillez sélectionner une gare de transport",
                icon: "error"
              });
              return;
            }

            axios
              .post(API_RESERVER_PROGRAMME_OFFRE, this.programmeReserved, {
                headers: HEADERS(this.$store.state.userAuthentified.token),
              })
              .then((response) => {
                if (response.status == 200) {
                  if (response.data.status.code == 800) {
                    /*this.creerHistoriquePaiement(
                      response.data.items[0].designation
                    );*/
                    this.successMsg = response.data.status.message;
                    showSuccessMessage();
                    this.$store.commit(
                      "STORE_RESERVATION_BILLET",
                      response.data.items[0]
                    );
                    this.$router
                      .push({ name: "DetailTicketReservation" })
                      .catch(() => {});
                  } else {

                    this.errorMsg = response.data.status.message;
                    showErrorMessage();
                  }
                } else if (response.status == 204) {
                  this.warningMsg = "Erreur , lors de la création";
                  showWarningMessage();
                } else {
                  this.errorMsg = "Erreur , opération de création impossible";
                  showErrorMessage();
                }
              })
              .catch((e) => {
                this.errorMsg = (e && e.message) || "Erreur lors de la réservation.";
                showErrorMessage();
              })
              .finally(() => {
                this.overlay = false;
              });
          }
          // Annulation : l'utilisateur revient simplement au formulaire
        });
    },

    formatAmount(value) {
      return Number(value || 0).toLocaleString("fr-FR");
    },

    // Complet uniquement si le nombre de places est connu et vaut 0 (Number(null) vaut aussi 0)
    isFull(programme) {
      const value = programme && programme.nombrePlaceDisponible;
      return value !== null && value !== undefined && value !== "" && Number(value) === 0;
    },

    selectProgramme(programme) {
      if (this.isFull(programme)) return;
      this.progChoosen = programme;
    },

    // CREER HISTORIQUE DE PAIEMENT
    async creerHistoriquePaiement(designationReservation) {
      let date = new Date();
      let year = date.getFullYear();
      let month = String(date.getMonth() + 1).padStart(2, "0");
      let day = String(date.getDate()).padStart(2, "0");
      let hours = String(date.getHours()).padStart(2, "0");
      let minutes = String(date.getMinutes()).padStart(2, "0");
      let seconds = String(date.getSeconds()).padStart(2, "0");
      let formattedDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      this.historyPaiement.data.reservationBilletVoyageDesignation =
        designationReservation;
      this.historyPaiement.data.dateTimePayment = formattedDate;
      this.historyPaiement.data.modePaiementDesignation = "ModePaiementEnEspece";
      await axios
        .post(API_CREATE_HISTORIQUE_PAIEMENT, this.historyPaiement, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else {
            this.errorMsg = "Erreur , lors la création de l'historique";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },


    //OBTENIR LA LISTE DES PROGRAMMES PAR OFFRE DE VOYAGE
    async getPlanningByOffersTravel() {
      this.offreVoyageObject.data.designation = this.offreVoyage.designation;
      await axios
        .post(
          API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
          this.offreVoyageObject,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          if (response.data.status.code == 800) {
            this.programmeList = response.data.items;
          } else {
            this.programmeList = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.programmeList = [];
        });
    },

    async retrieveOffreSelected() {
      if (localStorage.getItem("offreVoyageSelected")) {
        try {
          const offreVoyageEditing = JSON.parse(
            localStorage.getItem("offreVoyageSelected")
          );
          this.offreVoyage.id = offreVoyageEditing.id;
          this.offreVoyage.designation = offreVoyageEditing.designation;
          this.offreVoyage.description = offreVoyageEditing.description;
          this.offreVoyage.typeOffreVoyageDesignation =
            offreVoyageEditing.typeOffreVoyageDesignation;
          this.offreVoyage.compagnieTransportRaisonSociale =
            this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
          this.offreVoyage.villeDepartDesignation =
            offreVoyageEditing.villeDepartDesignation;
          this.offreVoyage.villeDestinationDesignation =
            offreVoyageEditing.villeDestinationDesignation;
          this.offreVoyageObject.data.designation =
            this.offreVoyage.designation;
          this.getPlanningByOffersTravel();
          await axios
            .post(API_RECUPERER_PRIX_PAR_OFFRE_VOYAGE, this.offreVoyageObject, {
              headers: HEADERS(this.$store.state.userAuthentified.token),
            })
            .then((response) => {
              if (response.data.status.code == 800) {
                this.prixEtModeParOffreVoyageList = response.data.items;
              } else {
                this.prixEtModeParOffreVoyageList = [];
              }
            })
            .catch((e) => {
              this.errorMsg = e;
              showErrorMessage();
            });
        } catch (error) {
          localStorage.removeItem("offreVoyageSelected");
        }
      }
    },

    async getGaresList() {
      if (this.isAdmin) {
        this.gareListObject.data.compagnieTransportRaisonSociale =
          this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        
        try {
          const response = await apiService.postForGet(
            'gares/getGareByCompagnieTransportRaisonScoiale',
            this.gareListObject
          );
          
          if (response.status.code != 800) {
            this.errorMsg = response.status.message;
            showErrorMessage();
            this.garesList = [];
          } else {
            this.garesList = response.items;
          }
        } catch (e) {
          this.errorMsg = e;
          showErrorMessage();
          this.garesList = [];
        }
      }
    },


  },

  mounted() {
    this.retrieveOffreSelected();
    this.getGaresList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.rb-kicker {
  margin-bottom: 4px;
}

.rb-route {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
  color: $ov-ink;
}

.rb-route__line {
  width: 26px;
  border-top: 2px dashed rgba(21, 158, 114, 0.55);
}

/* ÉTAPES */
.rb-steps {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid $ov-line;
  background: $ov-soft;
}

.rb-step-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  &--last {
    flex: 0 0 auto;
  }
}

.rb-step {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $ov-subtle;

  &--clickable {
    cursor: pointer;
  }

  &--active,
  &--done {
    color: $ov-navy;
  }
}

.rb-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 800;
  color: $ov-muted;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.14);

  .rb-step--active & {
    color: #ffffff;
    background: $ov-navy;
    border-color: $ov-navy;
    box-shadow: 0 0 0 4px rgba(12, 36, 97, 0.1);
  }

  .rb-step--done & {
    background: $ov-green;
    border-color: $ov-green;
  }
}

.rb-step__label {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.rb-step__line {
  flex: 1;
  min-width: 16px;
  height: 2px;
  border-radius: 2px;
  background: rgba(15, 23, 42, 0.1);

  &--done {
    background: $ov-green;
  }
}

.rb-panel__title {
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 800;
  color: $ov-navy;
}

.rb-narrow {
  max-width: 420px;
}

/* OPTIONS SÉLECTIONNABLES */
.rb-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rb-tarifs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.rb-option {
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(21, 158, 114, 0.45);
  }

  &--selected {
    border-color: $ov-green;
    background: rgba(21, 158, 114, 0.05);
    box-shadow: 0 0 0 3px rgba(21, 158, 114, 0.12);
  }

  &--disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--programme {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
  }

  &--tarif {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px;
  }
}

.rb-radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.25);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  .rb-option--selected & {
    border-color: $ov-green;
    box-shadow: inset 0 0 0 4px #ffffff, inset 0 0 0 10px $ov-green;
  }
}

.rb-prog__date {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 96px;
  padding-right: 14px;
  border-right: 1px solid $ov-line;
}

.rb-prog__day {
  font-size: 13px;
  font-weight: 800;
  color: $ov-navy;
}

.rb-prog__hour {
  font-size: 19px;
  font-weight: 900;
  color: $ov-green;
  font-variant-numeric: tabular-nums;
}

.rb-prog__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.rb-prog__title {
  font-size: 14px;
  font-weight: 800;
  color: $ov-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rb-prog__meta {
  font-size: 13px;
  color: $ov-muted;
}

.rb-tarif__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.rb-tarif__category {
  font-size: 13px;
  font-weight: 800;
  color: $ov-navy;
}

.rb-tarif__price {
  margin-top: 6px;
  font-size: 22px;
  font-weight: 900;
  color: $ov-ink;
  font-variant-numeric: tabular-nums;

  small {
    font-size: 12px;
    font-weight: 700;
    color: $ov-subtle;
  }
}

.rb-tarif__mode {
  font-size: 12px;
  color: $ov-muted;
}

/* TOTAL */
.rb-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-top: 1px solid $ov-line;
  background: rgba(21, 158, 114, 0.06);
  font-size: 13px;
  font-weight: 700;
  color: $ov-muted;

  strong {
    font-size: 22px;
    font-weight: 900;
    color: $ov-green-dark;
    font-variant-numeric: tabular-nums;
  }

  small {
    font-size: 12px;
  }
}

@media (max-width: 760px) {
  .rb-steps {
    padding: 14px 16px;
  }

  .rb-step__label {
    display: none;
  }

  .rb-step--active .rb-step__label {
    display: inline;
  }

  .rb-option--programme {
    flex-wrap: wrap;
  }
}
</style>
